import { toast } from "react-hot-toast";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string;

declare global {
  interface Window {
    google: any;
  }
}

export const getUserLocation = (): Promise<{ lat: number; lng: number }> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation not supported."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => {
        console.error("Geolocation Error:", err);
        reject(new Error("Failed to get location."));
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });
};

export const getAddressSuggestions = async (query: string): Promise<any[]> => {
  if (!query || query.length < 3) return [];
  try {
    const response = await fetch("https://places.googleapis.com/v1/places:autocomplete", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Goog-Api-Key": GOOGLE_MAPS_API_KEY },
      body: JSON.stringify({ input: query, includedRegionCodes: ["IN"] }),
    });
    const data = await response.json();
    if (data.error || !data.suggestions) return [];
    return data.suggestions.map((s: any) => ({
      place_id: s.placePrediction.placeId,
      display_name: s.placePrediction.text.text,
      structured_formatting: {
          main_text: s.placePrediction.structuredFormat.mainText.text,
          secondary_text: s.placePrediction.structuredFormat.secondaryText?.text
      }
    }));
  } catch (error) { 
    console.error("Error fetching address suggestions:", error);
    return []; 
  }
};

export const getPlaceDetails = async (placeId: string): Promise<{ lat: number; lng: number, address: string }> => {
  try {
    const response = await fetch(`https://places.googleapis.com/v1/places/${placeId}?fields=location,formattedAddress`, {
      method: "GET", 
      headers: { "X-Goog-Api-Key": GOOGLE_MAPS_API_KEY },
    });
    const data = await response.json();
    if (data.location) {
      return { 
        lat: data.location.latitude, 
        lng: data.location.longitude, 
        address: data.formattedAddress 
      };
    }
    throw new Error("No location found");
  } catch (error) { 
    console.error("Error fetching place details:", error);
    throw error; 
  }
};

export const geocodeAddress = async (address: string): Promise<{ lat: number; lng: number, display_name: string }> => {
  // Wait for Google Maps to be available (loaded by @react-google-maps/api)
  if (!window.google?.maps?.Geocoder) {
    throw new Error("Google Maps not loaded. Please wait for the map to initialize.");
  }
  
  const geocoder = new window.google.maps.Geocoder();
  return new Promise((resolve, reject) => {
    geocoder.geocode({ address, componentRestrictions: { country: "IN" } }, (results: any[], status: any) => {
      if (status === "OK" && results && results[0]) {
        resolve({ 
          lat: results[0].geometry.location.lat(), 
          lng: results[0].geometry.location.lng(), 
          display_name: results[0].formatted_address 
        });
      } else { 
        reject(new Error("Address not found.")); 
      }
    });
  });
};

export const getRoadDistance = async (start: { lat: number; lng: number }, end: { lat: number; lng: number }): Promise<number> => {
  // Fallback to straight-line distance if Google Maps not available
  if (!window.google?.maps?.DistanceMatrixService) {
    console.warn("Distance Matrix Service not available, using displacement calculation");
    return getDisplacement(start, end);
  }
  
  const service = new window.google.maps.DistanceMatrixService();
  return new Promise((resolve) => {
    service.getDistanceMatrix(
      { 
        origins: [new window.google.maps.LatLng(start.lat, start.lng)], 
        destinations: [new window.google.maps.LatLng(end.lat, end.lng)], 
        travelMode: window.google.maps.TravelMode.DRIVING, 
        region: 'in' 
      },
      (response: any, status: any) => {
        if (status === "OK" && response?.rows[0]?.elements[0]?.distance) {
          resolve(response.rows[0].elements[0].distance.value / 1000);
        } else {
          console.warn("Distance Matrix API failed, using displacement calculation");
          resolve(getDisplacement(start, end));
        }
      }
    );
  });
};

export const getDisplacement = (start: { lat: number; lng: number }, end: { lat: number; lng: number }) => {
  const R = 6371; // Earth's radius in km
  const dLat = (end.lat - start.lat) * (Math.PI / 180);
  const dLng = (end.lng - start.lng) * (Math.PI / 180);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + 
    Math.cos(start.lat * (Math.PI / 180)) * 
    Math.cos(end.lat * (Math.PI / 180)) * 
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};
